/**
 * Fechar camadas sobrepostas (popover, dropdown, menu, picker) — um só mecanismo para todo o v2.
 *
 * Por que não o "fundo invisível `fixed inset 0`": ele é filho do wrapper do gatilho, então (1) dentro de um `<label>`
 * o clique nele ativa o label, que "clica" no gatilho e reabre o popover (fechar → reabrir: parecia não fechar);
 * (2) num ancestral com `transform`/`backdrop-filter` (modais centrados com translateX) o `fixed` vira relativo a ele e
 * deixa de cobrir a tela; (3) num contexto de empilhamento (barra sticky z20) ele fica abaixo do menu lateral.
 * O Esc também falhava: cada modal registrava o próprio Esc antes do popover e o consumia primeiro.
 *
 * Agora: ouvintes globais na fase de captura da `window` (nenhum `stopPropagation` de componente os bloqueia) e uma
 * pilha de camadas abertas:
 *   - `pointerdown` fora do gatilho + painel (inclusive painel em portal) fecha as camadas do topo até a que contém o
 *     alvo — clicar num dropdown filho não fecha o pai; o clique seguinte é engolido se cair em algo não interativo
 *     (fundo de modal, texto de label), como fazia o fundo invisível;
 *   - Esc fecha só a camada do topo e devolve o foco ao gatilho (o modal por baixo não recebe o Esc);
 *   - clicar no gatilho alterna (ele conta como "dentro", quem fecha é o onClick dele);
 *   - `scroll: true` (painéis `fixed`/ancorados por coordenada): rolar fora do painel ou redimensionar fecha.
 */
import { useLayoutEffect, useRef, type RefObject } from 'react';
import { escapeTarget, isInside, layersToClose, shouldBlockLabelActivation, shouldSwallowClick, type DismissLayer } from './dismiss';

export type DismissReason = 'outside' | 'escape' | 'scroll';

export interface DismissOptions {
    /** Regiões "de dentro" (gatilho, painel, wrapper). Lidas a cada evento, então podem montar depois. */
    refs: readonly RefObject<Element | null>[];
    /** Para onde o foco volta no Esc (padrão: quem tinha o foco ao abrir). */
    trigger?: RefObject<HTMLElement | null>;
    /** Fecha ao tocar fora (padrão true). `false` = só Esc/gatilho. */
    outside?: boolean;
    /** Fecha com Esc (padrão true). */
    escape?: boolean;
    /** Fecha ao rolar fora do painel / redimensionar (padrão false). */
    scroll?: boolean;
}

interface Layer extends DismissLayer {
    scroll: boolean;
    close: (reason: DismissReason) => void;
    focusTarget: () => HTMLElement | null;
}

const stack: Layer[] = [];
let swallow: { target: EventTarget; at: number } | null = null;
let installed = false;

const targetOf = (e: Event): Node | null => ((e.composedPath?.()[0] ?? e.target) as Node | null);

function onPointerDown(e: PointerEvent) {
    swallow = null;
    if (!stack.length || e.button > 0) return;
    const target = targetOf(e);
    const { close, inside } = layersToClose(stack, target);
    if (!close.length) return;
    close.forEach((l) => l.close('outside'));
    if (target && shouldSwallowClick(true, inside, target)) swallow = { target, at: Date.now() };
}

function onClick(e: MouseEvent) {
    const target = targetOf(e);
    if (swallow) {
        const s = swallow;
        swallow = null;
        const same = target && (s.target === target || (s.target as Node).contains?.(target) || target.contains(s.target as Node));
        if (same && Date.now() - s.at < 1500) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
    }
    for (const l of stack) {
        const regions = l.regions();
        if (isInside(regions, target) && shouldBlockLabelActivation(target, regions)) { e.preventDefault(); return; }
    }
}

function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape' || e.isComposing) return;
    const l = escapeTarget(stack);
    if (!l) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const active = document.activeElement;
    const focus = l.focusTarget();
    const regions = l.regions();
    l.close('escape');
    if (focus?.isConnected && (!active || active === document.body || isInside(regions, active))) focus.focus();
}

function onScroll(e: Event) {
    const target = targetOf(e);
    for (const l of [...stack].reverse()) if (l.scroll && !isInside(l.regions(), target)) l.close('scroll');
}

function onResize() {
    for (const l of [...stack].reverse()) if (l.scroll) l.close('scroll');
}

function install() {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
}

/**
 * Registra uma camada aberta. `onClose` recebe o motivo; o gatilho continua alternando pelo próprio onClick.
 */
export function useDismiss(open: boolean, onClose: (reason: DismissReason) => void, opts: DismissOptions) {
    const latest = useRef({ onClose, opts });
    latest.current = { onClose, opts };

    useLayoutEffect(() => {
        if (!open) return;
        install();
        const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
        const layer: Layer = {
            regions: () => latest.current.opts.refs.map((r) => r.current),
            get outside() { return latest.current.opts.outside ?? true; },
            get escape() { return latest.current.opts.escape ?? true; },
            get scroll() { return latest.current.opts.scroll ?? false; },
            close: (reason) => latest.current.onClose(reason),
            focusTarget: () => latest.current.opts.trigger?.current ?? opener,
        };
        stack.push(layer);
        return () => {
            const i = stack.indexOf(layer);
            if (i >= 0) stack.splice(i, 1);
        };
    }, [open]);
}
